// Manager-bucket dock signal counts — KB gap inbox + AI prompting review queue.
//
// SC-5 slice of docs/planning/firefly_role_context_presentation.md §5.2.
// Centralizes the fetches the shell header banner and dock sections both
// need so we don't duplicate `/api/kb/admin/getGaps` + suggestions list
// round-trips.

import { useEffect, useMemo, useState } from "react";
import type { AuthContextValue } from "@/contexts/AuthContext";
import { canAccessPrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import { adminPost } from "@/lib/kbAdminClient";
import { listSuggestions } from "@/lib/aiPrompting/client";
import { rolesIncludeAiPromptingApply } from "@/lib/aiPrompting/roles";

export type ManagerDockSignals = {
  /** Open `kb_gap_items` rows for the scoped org; `null` when not fetched. */
  kbGapOpenCount: number | null;
  /** Open `ai_prompt_suggestions` rows; `null` when not fetched. */
  aiPromptingOpenCount: number | null;
};

export function useManagerDockSignals(
  auth: AuthContextValue | null,
  scopedOrganizationId: string | null,
): ManagerDockSignals {
  const [kbGapOpenCount, setKbGapOpenCount] = useState<number | null>(null);
  const [aiPromptingOpenCount, setAiPromptingOpenCount] = useState<number | null>(
    null,
  );

  const canFetchKbGaps = useMemo(
    () => !!auth && canAccessPrimaryMode("knowledgebase", auth),
    [auth],
  );

  const canFetchAiSuggestions = useMemo(() => {
    if (!auth || !canAccessPrimaryMode("aiPrompting", auth)) return false;
    return rolesIncludeAiPromptingApply(auth.roles);
  }, [auth]);

  useEffect(() => {
    if (!scopedOrganizationId) {
      setKbGapOpenCount(null);
      setAiPromptingOpenCount(null);
      return;
    }

    let cancelled = false;

    void (async () => {
      const fetches: Promise<void>[] = [];

      if (canFetchKbGaps) {
        fetches.push(
          adminPost<{ ok: true; openCount: number }>("/api/kb/admin/getGaps", {
            countOnly: true,
          }).then((r) => {
            if (!cancelled && r.ok) {
              setKbGapOpenCount(Number(r.data.openCount || 0));
            }
          }),
        );
      } else {
        setKbGapOpenCount(null);
      }

      if (canFetchAiSuggestions) {
        fetches.push(
          listSuggestions({ status: "open" }).then((r) => {
            if (!cancelled && r.ok) {
              setAiPromptingOpenCount(r.data.suggestions.length);
            }
          }),
        );
      } else {
        setAiPromptingOpenCount(null);
      }

      await Promise.all(fetches);
    })();

    return () => {
      cancelled = true;
    };
  }, [scopedOrganizationId, canFetchKbGaps, canFetchAiSuggestions]);

  return { kbGapOpenCount, aiPromptingOpenCount };
}
